import type { AllComponents } from "../interfaces/base";

/** Ensures legacy render paths receive an `id` when v2 rows omit numeric id. */
export function withStableComponentId<
  T extends Pick<AllComponents, "id" | "uuid">,
>(
  component: T,
  fallbackKey?: string
): T {
  if (component.id !== null && component.id !== undefined) {
    return component;
  }
  const identityKey = getComponentIdentityKey(component);
  return {
    ...component,
    id: component.uuid?.trim() || fallbackKey || identityKey,
  } as T;
}

/** Stable string key for React keys and map lookups (UUID when present, else numeric id). */
export function getComponentIdentityKey(
  component: Pick<AllComponents, "id" | "uuid"> | null | undefined
): string {
  if (!component) {
    return "";
  }
  const u = component.uuid?.trim();
  if (u) {
    return u;
  }
  if (component.id != null) {
    return String(component.id);
  }
  return "";
}

/**
 * DOM `id` for builder component roots: UUID first, then legacy id.
 * Omits invalid keys (use `|| undefined` on the attribute when empty).
 */
export function getBuilderDomId(props: {
  id?: number | string | null;
  uuid?: string | null;
}): string {
  const u = props.uuid?.trim();
  if (u) {
    return u;
  }
  if (props.id != null && props.id !== "") {
    return String(props.id);
  }
  return "";
}

/**
 * Resolves a component from `structure.components` when the map may be keyed by UUID
 * or legacy numeric string, or when the incoming key is a UUID but the map is still numeric-keyed.
 */
export function getComponentFromStructureMap(
  components: { [key: string]: AllComponents } | undefined,
  key: string | number | null | undefined
): AllComponents | undefined {
  if (!components || key === null || key === undefined) {
    return undefined;
  }
  const k = String(key).trim();
  if (!k) {
    return undefined;
  }
  const direct = components[k];
  if (direct) {
    return direct;
  }
  for (const c of Object.values(components)) {
    if (!c) continue;
    if (c.uuid?.trim() === k) {
      return c;
    }
    if (String(c.id) === k) {
      return c;
    }
  }
  return undefined;
}

/** V2 relations may expose `child_uuid`; v1 uses `child_id` only. */
export function getRelationChildKey(relation: {
  child_uuid?: string | null;
  child_id?: number | null;
}): string | null {
  const u = relation.child_uuid?.trim();
  if (u) {
    return u;
  }
  if (relation.child_id != null && relation.child_id !== undefined) {
    return String(relation.child_id);
  }
  return null;
}

/** V2 relations may expose `parent_uuid`; v1 uses `parent_id` only. */
export function getRelationParentKey(relation: {
  parent_uuid?: string | null;
  parent_id?: number | null;
}): string | null {
  const u = relation.parent_uuid?.trim();
  if (u) {
    return u;
  }
  if (relation.parent_id != null && relation.parent_id !== undefined) {
    return String(relation.parent_id);
  }
  return null;
}

/** Whether the rendered component is the same entity as the editor's active template root (hide duplicate in canvas). */
export function editorTemplateMatchesComponent(
  template: Pick<AllComponents, "id" | "uuid"> | null | false | undefined,
  component: AllComponents
): boolean {
  if (!template) {
    return false;
  }
  const tu = template.uuid?.trim();
  const cu = component.uuid?.trim();
  if (tu && cu && tu === cu) {
    return true;
  }
  if (template.id != null && component.id != null && template.id === component.id) {
    return true;
  }
  return false;
}

/**
 * Whether a text-editor target (uuid or id) points to this component.
 * Keeps editor behavior stable while moving selection to UUID-first.
 */
export function matchesEditorTarget(
  component: Pick<AllComponents, "id" | "uuid"> | null | undefined,
  target: string | number | null | undefined
): boolean {
  if (!component || target === null || target === undefined) {
    return false;
  }
  const targetKey = String(target).trim();
  if (!targetKey) {
    return false;
  }
  const componentUuid = component.uuid?.trim();
  if (componentUuid && componentUuid === targetKey) {
    return true;
  }
  if (component.id != null && String(component.id) === targetKey) {
    return true;
  }
  return false;
}
